import React, { useState, useEffect, useCallback } from "react";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { handleTokenAndPostRequest } from "../utils/utils";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT = "#10b981";
const GRAY_DARK = "#374151";
const WHITE = "#fff";

const GROWTH_FEATURES = [
  { text: "Unlimited activity notifications", desc: "Get notified every time a contact engages" },
  { text: "Monthly branded property updates", desc: "Automated emails to all your contacts with your branding" },
  { text: "Monthly branded suburb updates", desc: "Market insights sent automatically to your database" },
  { text: "AI news articles", desc: "Auto-generated, branded real estate articles" },
  { text: "AI Co-Pilot and outbound triggers", desc: "Smart conversation starters when contacts are active" },
  { text: "AI Infographics", desc: "Beautiful, shareable social media content" },
  { text: "CRM integration", desc: "Sync contacts from your existing CRM" },
  { text: "Customise branding", desc: "Your logo, colours, and profile on everything" },
  { text: "Dedicated support", desc: "Direct access to our team when you need help" },
  { text: "Vendorleads platform", desc: "Full access to detailed activity monitoring" },
];

const FAQ_ITEMS = [
  { q: "What happens after I subscribe?", a: "Your Growth plan activates immediately. All features unlock and your contacts will receive their first branded update in the next cycle." },
  { q: "Can I cancel anytime?", a: "Yes — no lock-in contracts. Email bayan@vendorleads.com.au and your plan remains active until the end of the billing period." },
  { q: "How does billing work?", a: "You're billed monthly via Stripe. Your pricing is based on your contact count. Cancel anytime." },
  { q: "What payment methods are accepted?", a: "All major credit and debit cards (Visa, Mastercard, Amex) via Stripe's secure checkout." },
];

const TOPUP_PRESETS = [10, 25, 50, 100, 250];

function formatAUD(amount) {
  if (amount == null || isNaN(amount)) return "A$0.00";
  return `A$${Number(amount).toFixed(2)}`;
}

export default function Billing() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);

  // Credit-balance state
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showReloadModal, setShowReloadModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState(25);
  const [topupLoading, setTopupLoading] = useState(false);
  const [reloadEnabled, setReloadEnabled] = useState(false);
  const [reloadThreshold, setReloadThreshold] = useState(10);
  const [reloadTopup, setReloadTopup] = useState(25);
  const [reloadSaving, setReloadSaving] = useState(false);
  const [reloadError, setReloadError] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchBilling = useCallback(() => {
    setLoading(true);
    handleTokenAndPostRequest(`${window.api_host}/billing/status`)
      .then((res) => setBilling(res.data))
      .catch(() => setBilling({ plan: "free" }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchBilling(); }, [fetchBilling]);

  const fetchBalance = useCallback(() => {
    setBalanceLoading(true);
    handleTokenAndPostRequest(`${window.api_host}/billing/balance`)
      .then((res) => {
        const b = res.data;
        setBalance(b);
        setReloadEnabled(!!b.auto_reload_enabled);
        setReloadThreshold(b.auto_reload_threshold_amount ?? 10);
        setReloadTopup(b.auto_reload_topup_amount ?? 25);
      })
      .catch(() => setBalance(null))
      .finally(() => setBalanceLoading(false));
  }, []);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  // Check for returning from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_id") || params.get("topup")) {
      window.history.replaceState({}, "", window.location.pathname);
      fetchBilling();
      fetchBalance();
    }
  }, [fetchBilling, fetchBalance]);

  const handleSubscribe = async () => {
    setCheckoutLoading(true);
    try {
      const baseUrl = window.location.origin;
      const res = await handleTokenAndPostRequest(`${window.api_host}/billing/create-checkout-session`, {
        success_url: `${baseUrl}/billing?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${baseUrl}/billing`,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      if (err?.response?.data?.already_subscribed) {
        fetchBilling();
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const res = await handleTokenAndPostRequest(`${window.api_host}/billing/create-portal-session`, {
        return_url: `${window.location.origin}/billing`,
      });
      if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch {
      // ignore
    } finally {
      setPortalLoading(false);
    }
  };

  const handleBuyCredit = async () => {
    setTopupLoading(true);
    try {
      const res = await handleTokenAndPostRequest(`${window.api_host}/billing/create-topup-checkout`, {
        amount: Number(topupAmount),
        return_url: `${window.location.origin}/billing`,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.error || "Could not start checkout");
    } finally {
      setTopupLoading(false);
    }
  };

  const handleSaveAutoReload = async (enabled) => {
    setReloadSaving(true);
    setReloadError("");
    try {
      await handleTokenAndPostRequest(`${window.api_host}/billing/auto-reload`, {
        enabled,
        threshold: Number(reloadThreshold),
        topup: Number(reloadTopup),
      });
      setReloadEnabled(enabled);
      setShowReloadModal(false);
      fetchBalance();
    } catch (err) {
      setReloadError(err?.response?.data?.error || "Could not save settings");
    } finally {
      setReloadSaving(false);
    }
  };

  const isGrowth = billing?.plan === "growth" && billing?.subscription_status === "active";
  const isPaused = billing?.plan === "paused" || billing?.subscription_status === "paused";
  const isCancelling = isGrowth && billing?.cancel_at_period_end;
  const isAgencyBilling = billing?.is_agency_billing;

  const formatDate = (ts) => {
    if (!ts) return "";
    return new Date(ts * 1000).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#f6f8fa" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: isMobile ? "80px 16px 40px" : "40px 24px 60px" }}>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: "#1f2937", margin: 0 }}>
            Billing
          </h1>
          <p style={{ color: "#6b7280", fontSize: 15, marginTop: 6 }}>
            Manage your subscription and payment details.
          </p>
        </div>

        {loading ? (
          <BillingSkeleton isMobile={isMobile} />
        ) : (
          <>
            {/* Current Plan Status */}
            <div style={{
              background: WHITE,
              borderRadius: 16,
              border: isGrowth ? `2px solid ${BRAND_ACCENT}` : isPaused ? "1px solid #f59e0b" : "1px solid #e2e8f0",
              padding: isMobile ? "20px 16px" : "24px",
              marginBottom: 24,
              boxShadow: isGrowth ? "0 4px 20px rgba(16,185,129,0.1)" : "none",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <span style={{
                      background: isGrowth ? "rgba(16,185,129,0.1)" : isPaused ? "rgba(245,158,11,0.1)" : "rgba(100,116,139,0.1)",
                      color: isGrowth ? BRAND_ACCENT : isPaused ? "#f59e0b" : "#64748b",
                      fontSize: "0.72rem", fontWeight: 700, borderRadius: 6,
                      padding: "3px 10px", textTransform: "uppercase", letterSpacing: "0.04em",
                    }}>
                      {isGrowth ? "Growth Plan" : isPaused ? "Subscription Paused" : "Free Plan"}
                    </span>
                    {isGrowth && (
                      <span style={{ color: BRAND_ACCENT, fontSize: "0.82rem", fontWeight: 600 }}>Active</span>
                    )}
                  </div>
                  {isGrowth ? (
                    <div style={{ marginTop: 8 }}>
                      <span style={{ fontSize: "1.8rem", fontWeight: 800, color: BRAND_COLOR }}>$150</span>
                      <span style={{ color: GRAY_DARK, fontSize: "0.9rem" }}> /month</span>
                      {isAgencyBilling && (
                        <p style={{ color: BRAND_ACCENT, fontSize: "0.82rem", fontWeight: 600, marginTop: 4 }}>
                          Managed by your agency
                        </p>
                      )}
                      {billing.current_period_end && !isAgencyBilling && (
                        <p style={{ color: GRAY_DARK, opacity: 0.5, fontSize: "0.82rem", marginTop: 4 }}>
                          {isCancelling
                            ? `Cancels on ${formatDate(billing.current_period_end)}`
                            : `Next billing date: ${formatDate(billing.current_period_end)}`}
                        </p>
                      )}
                    </div>
                  ) : isPaused ? (
                    <p style={{ color: GRAY_DARK, opacity: 0.6, fontSize: "0.9rem", marginTop: 6, maxWidth: 400 }}>
                      Your previous subscription has been paused. Subscribe to the Growth plan below to reactivate all features.
                    </p>
                  ) : (
                    <p style={{ color: GRAY_DARK, opacity: 0.6, fontSize: "0.9rem", marginTop: 6, maxWidth: 400 }}>
                      You're on the free plan. Upgrade to Growth to unlock AI automation, unlimited notifications, and branded campaigns.
                    </p>
                  )}
                </div>
                <div>
                  {isGrowth && !isAgencyBilling ? (
                    <button
                      onClick={handleManageBilling}
                      disabled={portalLoading}
                      style={{
                        border: `1px solid #e2e8f0`, borderRadius: 8,
                        padding: "10px 20px", fontSize: "0.88rem", fontWeight: 600,
                        cursor: portalLoading ? "wait" : "pointer",
                        background: WHITE, color: GRAY_DARK,
                        transition: "background 0.15s",
                      }}
                    >
                      {portalLoading ? <><Spinner animation="border" size="sm" style={{ marginRight: 6 }} />Loading…</> : "Manage Billing"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Extra usage / credit balance */}
            <div style={{
              background: WHITE, borderRadius: 16,
              border: "1px solid #e2e8f0",
              padding: isMobile ? "20px 16px" : "24px",
              marginBottom: 24,
            }}>
              <div style={{ marginBottom: 14 }}>
                <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#1f2937", margin: 0 }}>
                  Extra usage
                </h3>
                <p style={{ color: "#6b7280", fontSize: "0.85rem", margin: "4px 0 0" }}>
                  Buy extra usage so you can keep using AI features if you hit a limit.
                </p>
              </div>

              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 0", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 12,
              }}>
                <div>
                  <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#1f2937" }}>
                    {balanceLoading
                      ? <Skeleton width={70} height={20} />
                      : formatAUD(balance?.balance_amount ?? 0)}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>Current balance</div>
                </div>
                <button
                  onClick={() => { setTopupAmount(25); setShowBuyModal(true); }}
                  style={{
                    border: "1px solid #e2e8f0", borderRadius: 8,
                    padding: "8px 16px", fontSize: "0.85rem", fontWeight: 600,
                    background: WHITE, color: GRAY_DARK, cursor: "pointer",
                  }}
                >
                  Buy more
                </button>
              </div>

              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 0", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 12,
              }}>
                <div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#1f2937" }}>Auto-reload</div>
                  <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                    {balance?.auto_reload_enabled
                      ? `Top off to ${formatAUD(balance.auto_reload_topup_amount)} when your balance is ${formatAUD(balance.auto_reload_threshold_amount)}`
                      : "Off — turn on to keep your balance topped up automatically."}
                  </div>
                </div>
                <button
                  onClick={() => setShowReloadModal(true)}
                  style={{
                    border: "1px solid #e2e8f0", borderRadius: 8,
                    padding: "8px 16px", fontSize: "0.85rem", fontWeight: 600,
                    background: WHITE, color: GRAY_DARK, cursor: "pointer",
                  }}
                >
                  Manage
                </button>
              </div>
            </div>

            {/* Growth Plan Card — shown only when not subscribed (and not covered by agency) */}
            {!isGrowth && !isAgencyBilling && (
              <div style={{
                background: WHITE, borderRadius: 16,
                border: `2px solid ${BRAND_ACCENT}`,
                padding: isMobile ? "24px 16px" : "32px 28px",
                marginBottom: 24,
                position: "relative",
                boxShadow: "0 8px 30px rgba(16,185,129,0.12)",
              }}>
                <div style={{
                  position: "absolute", top: -12, right: 20,
                  background: BRAND_ACCENT, color: WHITE,
                  fontSize: "0.7rem", fontWeight: 700, borderRadius: 6,
                  padding: "3px 10px", textTransform: "uppercase", letterSpacing: "0.04em",
                }}>
                  Recommended
                </div>

                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
                  <span style={{
                    background: "rgba(16,185,129,0.1)", color: BRAND_ACCENT,
                    fontSize: "0.78rem", fontWeight: 700, borderRadius: 6,
                    padding: "3px 10px", textTransform: "uppercase", letterSpacing: "0.04em",
                  }}>
                    Growth
                  </span>
                </div>

                <div style={{ marginBottom: 4, marginTop: 16 }}>
                  <span style={{ fontSize: "2.4rem", fontWeight: 800, color: BRAND_COLOR }}>$150</span>
                  <span style={{ color: GRAY_DARK, fontSize: "0.95rem" }}> /month</span>
                </div>
                <p style={{ color: GRAY_DARK, fontSize: "0.8rem", opacity: 0.5, marginBottom: 24 }}>
                  Billed monthly — cancel anytime.
                </p>

                {/* Feature Grid */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "12px 24px",
                  marginBottom: 28,
                }}>
                  {GROWTH_FEATURES.map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 20, height: 20, borderRadius: "50%",
                        background: "rgba(16,185,129,0.12)", color: BRAND_ACCENT,
                        fontSize: "0.65rem", flexShrink: 0, marginTop: 2,
                      }}>✓</span>
                      <div>
                        <div style={{ fontSize: "0.88rem", color: GRAY_DARK, fontWeight: 600, lineHeight: 1.3 }}>{f.text}</div>
                        <div style={{ fontSize: "0.78rem", color: GRAY_DARK, opacity: 0.5, lineHeight: 1.4, marginTop: 1 }}>{f.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Everything in Free */}
                <div style={{
                  background: "#f8fafc", borderRadius: 10, padding: "12px 16px",
                  marginBottom: 24, border: "1px solid #e2e8f0",
                }}>
                  <div style={{ fontSize: "0.78rem", color: GRAY_DARK, opacity: 0.5, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    Plus everything in Free
                  </div>
                  <div style={{ fontSize: "0.82rem", color: GRAY_DARK, opacity: 0.6, lineHeight: 1.6 }}>
                    Download report page · Live valuation page · Branded suburb profiles · Comparable finder tool · 5 activity notifications/mo
                  </div>
                </div>

                <button
                  onClick={handleSubscribe}
                  disabled={checkoutLoading}
                  style={{
                    border: "none", borderRadius: 10,
                    padding: "14px 0", fontSize: "1rem", fontWeight: 700,
                    cursor: checkoutLoading ? "wait" : "pointer",
                    background: BRAND_ACCENT, color: WHITE,
                    boxShadow: "0 4px 14px rgba(16,185,129,0.4)",
                    transition: "transform 0.15s, box-shadow 0.15s",
                    width: "100%",
                  }}
                  onMouseOver={(e) => { if (!checkoutLoading) { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.5)"; } }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)"; }}
                >
                  {checkoutLoading ? <><Spinner animation="border" size="sm" style={{ marginRight: 6, color: "#fff" }} />Redirecting to checkout…</> : "Subscribe to Growth"}
                </button>

                <p style={{ textAlign: "center", fontSize: "0.78rem", color: GRAY_DARK, opacity: 0.4, marginTop: 12 }}>
                  Secure checkout powered by Stripe. Cancel anytime from this page.
                </p>
              </div>
            )}

            {/* FAQ */}
            <div style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: BRAND_COLOR, marginBottom: 16 }}>
                Frequently Asked Questions
              </h3>
              {FAQ_ITEMS.map((item, i) => (
                <div
                  key={i}
                  style={{
                    background: WHITE, borderRadius: 10,
                    border: "1px solid #e2e8f0", marginBottom: 8,
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                    style={{
                      width: "100%", textAlign: "left", background: "none", border: "none",
                      padding: "14px 16px", cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "space-between",
                    }}
                  >
                    <span style={{ fontSize: "0.9rem", fontWeight: 600, color: GRAY_DARK }}>{item.q}</span>
                    <span style={{ fontSize: "1.1rem", color: GRAY_DARK, opacity: 0.4, transform: faqOpen === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
                  </button>
                  {faqOpen === i && (
                    <div style={{ padding: "0 16px 14px", fontSize: "0.85rem", color: GRAY_DARK, opacity: 0.7, lineHeight: 1.6 }}>
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Terms link */}
            <p style={{ textAlign: "center", marginTop: 24, fontSize: "0.82rem", color: GRAY_DARK, opacity: 0.6 }}>
              By subscribing you agree to our{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: BRAND_COLOR, fontWeight: 600 }}
              >
                Terms and Conditions
              </a>
              .
            </p>
          </>
        )}
      </div>

      {/* Buy more credit modal */}
      {showBuyModal && (
        <ModalShell title="Buy extra usage" onClose={() => setShowBuyModal(false)}>
          <p style={{ fontSize: "0.85rem", color: "#6b7280", marginBottom: 14 }}>
            Choose how much credit to add. You'll be redirected to Stripe to enter
            your card details.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {TOPUP_PRESETS.map((amt) => (
              <button
                key={amt}
                onClick={() => setTopupAmount(amt)}
                style={{
                  flex: "1 1 60px",
                  padding: "10px 0",
                  borderRadius: 8,
                  border: `1px solid ${Number(topupAmount) === amt ? BRAND_ACCENT : "#e2e8f0"}`,
                  background: Number(topupAmount) === amt ? "rgba(16,185,129,0.08)" : WHITE,
                  color: Number(topupAmount) === amt ? BRAND_ACCENT : GRAY_DARK,
                  fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
                }}
              >
                A${amt}
              </button>
            ))}
          </div>
          <label style={{ fontSize: "0.8rem", color: "#6b7280", display: "block", marginBottom: 4 }}>
            Or enter a custom amount (AUD)
          </label>
          <input
            type="number"
            min={5}
            max={1000}
            step={1}
            value={topupAmount}
            onChange={(e) => setTopupAmount(e.target.value)}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8,
              border: "1px solid #e2e8f0", fontSize: "0.95rem", marginBottom: 18,
            }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowBuyModal(false)}
              style={{
                padding: "10px 16px", borderRadius: 8, border: "1px solid #e2e8f0",
                background: WHITE, color: GRAY_DARK, fontWeight: 600, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleBuyCredit}
              disabled={topupLoading || !topupAmount || Number(topupAmount) < 5}
              style={{
                padding: "10px 18px", borderRadius: 8, border: "none",
                background: BRAND_ACCENT, color: WHITE, fontWeight: 700,
                cursor: topupLoading ? "wait" : "pointer", minWidth: 120,
              }}
            >
              {topupLoading ? <Spinner animation="border" size="sm" /> : `Buy A$${Number(topupAmount || 0).toFixed(0)}`}
            </button>
          </div>
        </ModalShell>
      )}

      {/* Adjust auto-reload modal */}
      {showReloadModal && (
        <ModalShell title="Adjust auto-reload" onClose={() => setShowReloadModal(false)}>
          <p style={{ fontSize: "0.85rem", color: "#6b7280", marginBottom: 14 }}>
            You can automatically buy more extra usage when you run low.
          </p>
          <label style={{ fontSize: "0.8rem", color: "#6b7280", display: "block", marginBottom: 4 }}>
            When extra usage is:
          </label>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <span style={{ position: "absolute", left: 12, top: 10, color: "#6b7280", fontSize: "0.9rem" }}>AUD</span>
            <input
              type="number" min={1} max={500} step={1} value={reloadThreshold}
              onChange={(e) => setReloadThreshold(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px 10px 48px", borderRadius: 8,
                border: "1px solid #e2e8f0", fontSize: "0.95rem",
              }}
            />
          </div>
          <label style={{ fontSize: "0.8rem", color: "#6b7280", display: "block", marginBottom: 4 }}>
            Top up to:
          </label>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <span style={{ position: "absolute", left: 12, top: 10, color: "#6b7280", fontSize: "0.9rem" }}>AUD</span>
            <input
              type="number" min={5} max={1000} step={1} value={reloadTopup}
              onChange={(e) => setReloadTopup(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px 10px 48px", borderRadius: 8,
                border: "1px solid #e2e8f0", fontSize: "0.95rem",
              }}
            />
          </div>
          <p style={{ fontSize: "0.78rem", color: "#6b7280", marginBottom: 16 }}>
            You agree that we'll charge the card you have on file for the amount above
            on a recurring basis whenever your balance reaches the threshold. To cancel,
            turn off auto-reload.
          </p>
          {!balance?.has_payment_method && (
            <p style={{ fontSize: "0.8rem", color: "#b45309", background: "#fef3c7", padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>
              You'll need to make a one-off purchase first so we can save your card.
            </p>
          )}
          {reloadError && (
            <p style={{ fontSize: "0.82rem", color: "#b91c1c", marginBottom: 12 }}>{reloadError}</p>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {balance?.auto_reload_enabled && (
              <button
                onClick={() => handleSaveAutoReload(false)}
                disabled={reloadSaving}
                style={{
                  padding: "10px 16px", borderRadius: 8, border: "1px solid #e2e8f0",
                  background: WHITE, color: GRAY_DARK, fontWeight: 600, cursor: "pointer",
                }}
              >
                Turn off
              </button>
            )}
            <button
              onClick={() => handleSaveAutoReload(true)}
              disabled={reloadSaving || !balance?.has_payment_method}
              style={{
                padding: "10px 18px", borderRadius: 8, border: "none",
                background: BRAND_COLOR, color: WHITE, fontWeight: 700,
                cursor: reloadSaving ? "wait" : "pointer", minWidth: 80,
                opacity: !balance?.has_payment_method ? 0.5 : 1,
              }}
            >
              {reloadSaving ? <Spinner animation="border" size="sm" /> : "Set"}
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function BillingSkeleton({ isMobile }) {
  const cardBase = {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    padding: isMobile ? "20px 16px" : "24px",
    marginBottom: 24,
  };
  return (
    <>
      {/* Current Plan Status skeleton */}
      <div style={cardBase}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Skeleton width={110} height={22} borderRadius={6} style={{ marginBottom: 12 }} />
            <Skeleton width={140} height={32} style={{ marginBottom: 6 }} />
            <Skeleton width={200} height={12} />
          </div>
          <Skeleton width={140} height={40} borderRadius={8} />
        </div>
      </div>

      {/* Extra usage skeleton */}
      <div style={cardBase}>
        <div style={{ marginBottom: 14 }}>
          <Skeleton width={120} height={18} style={{ marginBottom: 6 }} />
          <Skeleton width="70%" height={12} />
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 0", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <Skeleton width={80} height={20} style={{ marginBottom: 4 }} />
            <Skeleton width={100} height={11} />
          </div>
          <Skeleton width={90} height={36} borderRadius={8} />
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 0", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Skeleton width={100} height={16} style={{ marginBottom: 4 }} />
            <Skeleton width="80%" height={11} />
          </div>
          <Skeleton width={90} height={36} borderRadius={8} />
        </div>
      </div>

      {/* Growth Plan Card skeleton */}
      <div style={{
        ...cardBase,
        padding: isMobile ? "24px 16px" : "32px 28px",
      }}>
        <Skeleton width={70} height={22} borderRadius={6} style={{ marginBottom: 18 }} />
        <Skeleton width={160} height={42} style={{ marginBottom: 6 }} />
        <Skeleton width={180} height={11} style={{ marginBottom: 24 }} />
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: "12px 24px",
          marginBottom: 28,
        }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Skeleton circle width={20} height={20} />
              <div style={{ flex: 1 }}>
                <Skeleton width="80%" height={14} style={{ marginBottom: 4 }} />
                <Skeleton width="95%" height={11} />
              </div>
            </div>
          ))}
        </div>
        <Skeleton height={48} borderRadius={10} />
      </div>

      {/* FAQ skeleton */}
      <div style={{ marginTop: 16 }}>
        <Skeleton width={240} height={22} style={{ marginBottom: 16 }} />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <Skeleton height={48} borderRadius={10} />
          </div>
        ))}
      </div>
    </>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, padding: "20px 22px",
          width: "100%", maxWidth: 440,
          boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "#1f2937" }}>{title}</h3>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "1.4rem", color: "#6b7280", cursor: "pointer", lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
